// Browser Back or Forward that leaves a page with a dirty edit-stack screen. BrowserRouter has no
// blocker, and a pathname change unmounts the host with its draft, so this window popstate listener
// steps in first: it hides the jump from the router, steps history back to the host's entry and
// asks. Discard repeats the jump with nothing dirty, so it goes through in one step.
//
// It must run before the router's own popstate listener (registered when BrowserRouter mounts), so
// this module is imported for its side effect at the top of src/index.tsx: listeners on one target
// run in registration order, and a capture listener on window can't be relied on to run first.
// Pops that keep the pathname are left to the host's URL effect, which re-pushes and asks itself.

export interface GuardEntry {
  pathname: string;
  href: string;
  state: unknown;
}

export interface LeaveGuardHost {
  /** True while any of the host's screens has unsaved edits. */
  isDirty: () => boolean;
  clearDirty: () => void;
  /** The history entry the host is showing. */
  here: () => GuardEntry | null;
  /** Opens the host's discard confirm; the Discard button runs `onDiscard`. */
  ask: (onDiscard: () => void) => void;
}

const hosts = new Set<LeaveGuardHost>();

/** Adds an edit-stack host to the guard; returns the unregister function. */
export function registerLeaveGuard(host: LeaveGuardHost): () => void {
  hosts.add(host);
  return () => {
    hosts.delete(host);
  };
}

/** The current history entry, for a host's `here` snapshot. */
export function currentEntry(): GuardEntry {
  return {
    pathname: window.location.pathname,
    href: window.location.href,
    state: window.history.state
  };
}

// The router keeps its entry index in history.state.idx; without it the guard re-pushes the entry.
const idxOf = (state: unknown) => {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
};

let swallowNext = false;

function onPopState(e: PopStateEvent) {
  if (swallowNext) {
    // Our own step back to the host's entry: the router never saw the jump away, so it must not
    // see this either.
    swallowNext = false;
    e.stopImmediatePropagation();
    return;
  }
  const dirtyHosts = [...hosts].filter((h) => {
    const from = h.here();
    return !!from && h.isDirty() && window.location.pathname !== from.pathname;
  });
  if (!dirtyHosts.length) return;
  const host = dirtyHosts[0];
  const from = host.here() as GuardEntry;
  e.stopImmediatePropagation();

  const fromIdx = idxOf(from.state);
  const toIdx = idxOf(window.history.state);
  let leave: () => void;
  if (fromIdx !== null && toIdx !== null && toIdx !== fromIdx) {
    const delta = toIdx - fromIdx;
    swallowNext = true;
    window.history.go(-delta);
    leave = () => window.history.go(delta);
  } else {
    window.history.pushState(from.state, '', from.href);
    leave = () => window.history.back();
  }
  host.ask(() => {
    dirtyHosts.forEach((h) => h.clearDirty());
    leave();
  });
}

window.addEventListener('popstate', onPopState, true);
